import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, Globe, Check, Pencil, Trash2, Save, X, Reply } from "lucide-react";
import api, { formatErr } from "../../lib/api";

export default function AdmEnquiries() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [responding, setResponding] = useState(null);

  const refresh = () => api.get("/admin/enquiries").then((r) => setList(r.data));
  useEffect(() => { refresh(); }, []);

  const markResponded = async (e) => {
    try { await api.put(`/admin/enquiries/${e.id}`, { status: "responded" }); toast.success("Marked as responded"); refresh(); }
    catch (err) { toast.error(formatErr(err)); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete enquiry from ${e.name}?`)) return;
    try { await api.delete(`/admin/enquiries/${e.id}`); toast.success("Deleted"); refresh(); }
    catch (err) { toast.error(formatErr(err)); }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/admin/enquiries/${editing.id}`, {
        name: editing.name, email: editing.email, mobile: editing.mobile, message: editing.message, status: editing.status,
      });
      toast.success("Updated"); setEditing(null); refresh();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const saveResponse = async () => {
    try {
      await api.put(`/admin/enquiries/${responding.id}`, { status: "responded", response: responding.response });
      toast.success("Response saved"); setResponding(null); refresh();
    } catch (err) { toast.error(formatErr(err)); }
  };

  return (
    <div className="space-y-6">
      <div><div className="overline mb-2">Inbox</div><h1 className="font-display text-4xl">Chatbot Enquiries</h1></div>
      <div className="space-y-3">
        {list.map((e) => (
          <div key={e.id} className="card-luxe p-5" data-testid={`enquiry-${e.id}`}>
            <div className="flex justify-between items-start mb-2 gap-3 flex-wrap">
              <div>
                <div className="overline">{e.type}</div>
                <div className="font-display text-lg mt-1">{e.name}</div>
                <div className="flex gap-4 text-xs text-white/50 mt-1 flex-wrap">
                  <span className="flex items-center gap-1"><Mail size={11}/>{e.email}</span>
                  <span className="flex items-center gap-1"><Phone size={11}/>{e.mobile}</span>
                  {e.geo?.country && <span className="flex items-center gap-1"><Globe size={11}/>{e.geo.city}, {e.geo.country}</span>}
                </div>
              </div>
              <span className={`text-[10px] uppercase tracking-luxe px-2 py-1 ${e.status === "responded" ? "bg-[#32D74B]/10 text-[#32D74B]" : "bg-copper/10 text-copper"}`}>
                {e.status === "responded" ? "Responded" : "Pending"}
              </span>
            </div>
            <p className="text-sm text-white/70 mt-2 whitespace-pre-line">{e.message}</p>
            {e.attachment_data && (
              <div className="mt-3">
                <div className="text-[10px] uppercase tracking-luxe text-white/40 mb-1">Attachment{e.attachment_name ? ` — ${e.attachment_name}` : ""}</div>
                <a href={e.attachment_data} target="_blank" rel="noreferrer" data-testid="enquiry-attachment">
                  <img src={e.attachment_data} alt={e.attachment_name || "attachment"} className="max-h-48 border border-white/10 hover:border-copper transition-colors object-contain bg-white/5"/>
                </a>
              </div>
            )}
            {e.order_id && <div className="text-xs text-white/40 mt-2">Order: {e.order_id}</div>}
            {e.response && (
              <div className="mt-3 p-3 border border-copper/30 bg-copper/5">
                <div className="overline mb-1">Your Response</div>
                <p className="text-sm whitespace-pre-line">{e.response}</p>
              </div>
            )}
            <div className="text-xs text-white/40 mt-2">{new Date(e.created_at).toLocaleString()}</div>
            <div className="flex gap-2 mt-3 flex-wrap">
              {e.status !== "responded" && (
                <button data-testid={`enquiry-respond-${e.id}`} onClick={() => setResponding({ ...e, response: e.response || "" })} className="btn-primary text-xs"><Reply size={12}/> Respond</button>
              )}
              {e.status !== "responded" && (
                <button data-testid={`enquiry-mark-${e.id}`} onClick={() => markResponded(e)} className="btn-outline text-xs"><Check size={12}/> Mark Responded</button>
              )}
              <button data-testid={`enquiry-edit-${e.id}`} onClick={() => setEditing({ ...e })} className="btn-outline text-xs"><Pencil size={12}/> Edit</button>
              <button data-testid={`enquiry-delete-${e.id}`} onClick={() => remove(e)} className="text-xs uppercase tracking-luxe px-3 py-2 border border-red-500/40 text-red-400 hover:bg-red-500/10 flex items-center gap-1.5"><Trash2 size={12}/> Delete</button>
            </div>
          </div>
        ))}
        {list.length === 0 && <div className="card-luxe p-10 text-center text-white/40">No enquiries yet</div>}
      </div>

      {editing && (
        <div data-testid="enquiry-edit-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 maintenance-blur" onClick={() => setEditing(null)}>
          <div className="card-luxe p-6 max-w-2xl w-full" onClick={(ev) => ev.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <div className="font-display text-2xl">Edit Enquiry</div>
              <button onClick={() => setEditing(null)} className="p-2 hover:text-copper"><X size={16}/></button>
            </div>
            <div className="space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Name</div>
                  <input data-testid="enquiry-edit-name" className="input-box" value={editing.name || ""} onChange={(ev) => setEditing({ ...editing, name: ev.target.value })}/>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Email</div>
                  <input data-testid="enquiry-edit-email" className="input-box" value={editing.email || ""} onChange={(ev) => setEditing({ ...editing, email: ev.target.value })}/>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Mobile</div>
                  <input data-testid="enquiry-edit-mobile" className="input-box" value={editing.mobile || ""} onChange={(ev) => setEditing({ ...editing, mobile: ev.target.value })}/>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Status</div>
                  <select data-testid="enquiry-edit-status" className="input-box" value={editing.status || "not_responded"} onChange={(ev) => setEditing({ ...editing, status: ev.target.value })}>
                    <option value="not_responded">Pending</option>
                    <option value="responded">Responded</option>
                  </select>
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Message</div>
                <textarea data-testid="enquiry-edit-message" className="input-box" rows={5} value={editing.message || ""} onChange={(ev) => setEditing({ ...editing, message: ev.target.value })}/>
              </div>
            </div>
            <div className="flex gap-3 mt-5">
              <button data-testid="enquiry-edit-save" onClick={saveEdit} className="btn-primary"><Save size={13}/> Save</button>
              <button onClick={() => setEditing(null)} className="btn-outline">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {responding && (
        <div data-testid="enquiry-respond-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 maintenance-blur" onClick={() => setResponding(null)}>
          <div className="card-luxe p-6 max-w-2xl w-full" onClick={(ev) => ev.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <div className="font-display text-2xl">Respond to {responding.name}</div>
              <button onClick={() => setResponding(null)} className="p-2 hover:text-copper"><X size={16}/></button>
            </div>
            <div className="text-sm text-white/60 mb-3 p-3 bg-white/5 border border-white/10">
              <div className="overline mb-1">Original Message</div>
              <p className="whitespace-pre-line">{responding.message}</p>
            </div>
            <textarea data-testid="enquiry-response-text" className="input-box" rows={6} placeholder="Type your response…" value={responding.response} onChange={(ev) => setResponding({ ...responding, response: ev.target.value })}/>
            <div className="flex gap-3 mt-4">
              <button data-testid="enquiry-response-save" onClick={saveResponse} className="btn-primary"><Save size={13}/> Save Response</button>
              <button onClick={() => setResponding(null)} className="btn-outline">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
